import type { BridgeChartTheme } from "../themes/defaultTheme";
import type {
  BridgeChartIntelligenceMarker,
  BridgeChartMarker,
  BridgeChartStudyCode,
  BridgeChartStudyState,
  NormalizedBridgeChartData,
  NormalizedIndicatorSeries
} from "../types/data";
import { buildPriceScale, type PriceScale } from "../scales/priceScale";
import { barWidth, clamp, indexFromX, xForIndex } from "../scales/timeScale";
import { mapCandle } from "../series/candles";
import { mapOverlayPoints } from "../overlays/indicatorOverlay";
import { mapMarker, normalizeIntelligenceMarkers, resolveMarkerCollisions } from "../overlays/markers";
import { escapeHtml, formatNumber, shortDate } from "../tools/format";
import { evidenceKey } from "../core/data";
import { mapPanePoints } from "../studies/paneStudy";
import type {
  BridgeChartDrawing,
  BridgeChartDrawingPoint,
  BridgeChartDrawingState,
  BridgeChartPaneOptions,
  BridgeChartPaneState,
  BridgeChartPerformanceBudget,
  BridgeChartPerformanceState
} from "../types/public";
import { fibonacciLevels } from "../drawings/fibonacci";
import { createDrawingTimeAxis, drawingTransform as makeDrawingTransform, drawingIdentity, type DrawingTimeAxis, type DrawingTransform } from "../drawings/marketCoordinates";
import { buildLayout, type ChartLayout } from "./layout";

export interface PointerPoint {
  x: number;
  y: number;
}

export class CanvasRenderer {
  readonly canvas: HTMLCanvasElement;
  readonly tooltip?: HTMLElement;
  private readonly ctx: CanvasRenderingContext2D;
  private data: NormalizedBridgeChartData;
  private drawingAxis: DrawingTimeAxis;
  private drawingPriceBasis = "fixed-price";
  private theme: BridgeChartTheme;
  private layout: ChartLayout | null = null;
  private dpr = 1;
  private readonly minVisibleBars: number;
  private readonly maxDevicePixelRatio: number;
  private paneOptions: BridgeChartPaneOptions;
  private readonly enabledStudyCodes = new Set<BridgeChartStudyCode>();
  private showMarkers = true;
  private intelligenceMarkers: BridgeChartIntelligenceMarker[] = [];
  private strategyMarkersByDate = new Map<string, BridgeChartMarker[]>();
  private tradeIdeaMarkersByDate = new Map<string, BridgeChartMarker[]>();
  private intelligenceMarkersByDate = new Map<string, BridgeChartIntelligenceMarker[]>();
  private drawingState: Pick<BridgeChartDrawingState, "drawings" | "selectedId"> = { drawings: [], selectedId: null };
  private drawCountValue = 0;
  private lastDrawWorkUnitsValue = 0;
  private fullDataAssignmentsValue = 1;
  private incrementalDataAssignmentsValue = 0;
  viewStart = 0;
  viewEnd = 0;
  crosshair: PointerPoint | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    tooltip: HTMLElement | undefined,
    data: NormalizedBridgeChartData,
    theme: BridgeChartTheme,
    interaction: { minVisibleBars: number; maxDevicePixelRatio: number },
    paneOptions: BridgeChartPaneOptions = {},
    private readonly studyDefaults?: readonly BridgeChartStudyCode[]
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("BridgeChart requires a 2D canvas context.");
    this.canvas = canvas;
    this.tooltip = tooltip;
    this.ctx = ctx;
    this.theme = theme;
    this.data = data;
    this.drawingAxis = createDrawingTimeAxis(data.bars.map(bar => bar.date), data.metadata.timeframe);
    this.minVisibleBars = interaction.minVisibleBars;
    this.maxDevicePixelRatio = interaction.maxDevicePixelRatio;
    this.paneOptions = clonePaneOptions(paneOptions);
    this.intelligenceMarkers = normalizeIntelligenceMarkers(data.intelligenceMarkers || []);
    this.rebuildMarkerIndexes();
    this.resetStudies();
    this.resetView();
  }

  setData(data: NormalizedBridgeChartData): void {
    this.data = data;
    this.drawingAxis = createDrawingTimeAxis(data.bars.map(bar => bar.date), data.metadata.timeframe);
    this.intelligenceMarkers = normalizeIntelligenceMarkers(data.intelligenceMarkers || []);
    this.rebuildMarkerIndexes();
    this.fullDataAssignmentsValue++;
    this.resetStudies();
    this.resetView();
    this.draw();
  }

  setIncrementalData(data: NormalizedBridgeChartData): void {
    const previousLength = this.data.bars.length;
    const previousSpan = Math.max(0, this.viewEnd - this.viewStart + 1);
    const pinnedToLatest = previousLength === 0 || this.viewEnd === previousLength - 1;
    this.data = data;
    this.drawingAxis = createDrawingTimeAxis(data.bars.map(bar => bar.date), data.metadata.timeframe);
    this.intelligenceMarkers = normalizeIntelligenceMarkers(data.intelligenceMarkers || []);
    this.rebuildMarkerIndexes();
    this.incrementalDataAssignmentsValue++;
    if (data.bars.length === 0) {
      this.viewStart = 0;
      this.viewEnd = -1;
    } else if (pinnedToLatest) {
      this.viewEnd = data.bars.length - 1;
      this.viewStart = Math.max(0, this.viewEnd - Math.max(0, previousSpan - 1));
    } else {
      this.viewStart = clamp(this.viewStart, 0, data.bars.length - 1);
      this.viewEnd = clamp(this.viewEnd, this.viewStart, data.bars.length - 1);
    }
    this.constrainViewToBudget();
    this.rebuildLayout();
    this.draw();
  }

  performanceState(): BridgeChartPerformanceState {
    const budget = performanceBudget(this.layout?.width ?? 720);
    return {
      profile: budget.profile,
      budget: { ...budget },
      retainedBars: this.data.bars.length,
      visibleBars: Math.max(0, this.viewEnd - this.viewStart + 1),
      lastDrawWorkUnits: this.lastDrawWorkUnitsValue,
      drawCount: this.drawCountValue,
      fullDataAssignments: this.fullDataAssignmentsValue,
      incrementalDataAssignments: this.incrementalDataAssignmentsValue
    };
  }

  setStudyVisibility(code: BridgeChartStudyCode, visible: boolean): void {
    const study = this.data.indicators.find(series => series.code === code);
    if (!study) throw new TypeError(`BridgeChart study '${code}' is not present in the current data.`);
    if (visible && !study.available) {
      throw new TypeError(`BridgeChart study '${code}' is unavailable: ${study.unavailableReason}.`);
    }
    if (visible) this.enabledStudyCodes.add(code);
    else this.enabledStudyCodes.delete(code);
    this.rebuildLayout();
    this.draw();
  }

  setPaneHeight(code: BridgeChartStudyCode, height: number | null): void {
    const heights = { ...(this.paneOptions.heights ?? {}) };
    if (height === null) delete heights[code];
    else heights[code] = height;
    const next = { ...this.paneOptions, heights };
    if (this.layout) buildLayout(this.layout.width, this.layout.height, this.renderedPaneCodes(), next);
    this.paneOptions = next;
    this.rebuildLayout();
    this.draw();
  }

  paneStates(): BridgeChartPaneState[] {
    if (!this.layout) return [];
    const viewport = { start: this.viewStart, end: this.viewEnd };
    const crosshairIndex = this.crosshair ? this.indexFromX(this.crosshair.x) : null;
    return this.layout.panes.map(pane => ({
      code: pane.code,
      top: pane.top,
      height: pane.height,
      viewport: { ...viewport },
      crosshairIndex
    }));
  }

  studyStates(): BridgeChartStudyState[] {
    return this.data.indicators.map(series => ({
      code: series.code,
      label: series.label,
      kind: series.kind,
      available: series.available,
      enabled: series.available && this.enabledStudyCodes.has(series.code),
      unavailableReason: series.unavailableReason
    }));
  }

  setTheme(theme: BridgeChartTheme): void {
    this.theme = theme;
    this.draw();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const parent = this.canvas.parentElement;
    const fill = this.canvas.dataset?.bridgeChartFill === "true";
    const width = Math.max(320, Math.floor((fill ? parent?.clientWidth : rect.width) || 720));
    const height = Math.max(300, Math.floor((fill ? parent?.clientHeight : rect.height) || 360));
    this.dpr = Math.max(1, Math.min(this.maxDevicePixelRatio, window.devicePixelRatio || 1));
    this.canvas.width = Math.floor(width * this.dpr);
    this.canvas.height = Math.floor(height * this.dpr);
    this.canvas.style.width = fill ? "100%" : `${width}px`;
    this.canvas.style.height = fill ? "100%" : `${height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.layout = buildLayout(width, height, this.renderedPaneCodes(), this.paneOptions);
    this.constrainViewToBudget();
    this.draw();
  }

  setView(start: number, end: number): void {
    if (this.data.bars.length === 0) {
      this.viewStart = 0;
      this.viewEnd = -1;
      this.draw();
      return;
    }
    let span = end - start + 1;
    const maximumVisible = performanceBudget(this.layout?.width ?? 720).maxVisibleBars;
    if (span > maximumVisible) {
      end = Math.min(this.data.bars.length - 1, end);
      start = Math.max(0, end - maximumVisible + 1);
      span = end - start + 1;
    }
    if (span >= this.data.bars.length) {
      this.viewStart = 0;
      this.viewEnd = this.data.bars.length - 1;
    } else {
      if (start < 0) {
        end -= start;
        start = 0;
      }
      if (end >= this.data.bars.length) {
        start -= end - this.data.bars.length + 1;
        end = this.data.bars.length - 1;
      }
      this.viewStart = clamp(start, 0, this.data.bars.length - 1);
      this.viewEnd = clamp(end, this.viewStart, this.data.bars.length - 1);
    }
    this.draw();
  }

  zoomAround(index: number, factor: number): void {
    const total = this.data.bars.length;
    if (total === 0 || !Number.isFinite(factor) || factor <= 0) return;
    const span = this.viewEnd - this.viewStart + 1;
    const nextSpan = Math.max(Math.min(this.minVisibleBars, total), Math.min(total, Math.round(span * factor)));
    const ratio = span <= 1 ? 0.5 : Math.max(0, Math.min(1, (index - this.viewStart) / span));
    const nextStart = Math.round(index - nextSpan * ratio);
    this.setView(nextStart, nextStart + nextSpan - 1);
  }

  fitContent(): void {
    this.setView(0, this.data.bars.length - 1);
  }

  barWidth(): number {
    return this.layout ? barWidth(this.layout, this.viewStart, this.viewEnd) : 1;
  }

  indexFromX(x: number): number {
    return this.layout ? indexFromX(this.layout, this.viewStart, this.viewEnd, x) : 0;
  }

  setDrawings(state: Pick<BridgeChartDrawingState, "drawings" | "selectedId">): void {
    this.drawingState = {
      drawings: state.drawings.map(drawing => ({ ...drawing, start: { ...drawing.start }, end: { ...drawing.end } })),
      selectedId: state.selectedId
    };
    this.draw();
  }

  drawingPointFromCanvas(point: PointerPoint): BridgeChartDrawingPoint {
    if (!this.layout) return { x: 0, y: 0 };
    return {
      x: clamp((point.x - this.layout.left) / Math.max(1, this.layout.plotWidth), 0, 1),
      y: clamp((point.y - this.layout.top) / Math.max(1, this.layout.plotHeight), 0, 1)
    };
  }

  setDrawingPriceBasis(value: string): void { this.drawingPriceBasis = drawingIdentity(value); this.draw(); }

  drawingTransform(priceScale?: PriceScale): DrawingTransform | null {
    if (!this.layout || !this.data.bars.length) return null;
    const identities = [this.data.metadata.symbol, this.data.metadata.timeframe];
    if (identities.some(value => typeof value !== "string" || !value.trim() || value !== value.trim() || value.length > 128)) return null;
    const visible = this.data.bars.slice(this.viewStart, this.viewEnd + 1);
    const scale = priceScale ?? buildPriceScale(visible, this.visibleActiveStudies(visible).filter(series => series.kind === "Overlay"), this.viewStart, this.viewEnd, this.layout);
    return makeDrawingTransform(this.drawingAxis, {
      symbol: this.data.metadata.symbol, timeframe: this.data.metadata.timeframe, priceBasis: this.drawingPriceBasis,
      viewStart: this.viewStart, viewEnd: this.viewEnd, minimumPrice: scale.min, maximumPrice: scale.max
    });
  }

  draw(): void {
    if (!this.layout) return;
    this.drawCountValue++;
    this.ensurePaneLayout();
    this.ctx.clearRect(0, 0, this.layout.width, this.layout.height);
    if (this.data.bars.length === 0) {
      this.lastDrawWorkUnitsValue = 1;
      return;
    }
    const visible = this.data.bars.slice(this.viewStart, this.viewEnd + 1);
    const active = this.visibleActiveStudies(visible);
    const overlays = active.filter(series => series.kind === "Overlay");
    const panes = active.filter(series => series.kind === "Pane");
    const priceScale = buildPriceScale(visible, overlays, this.viewStart, this.viewEnd, this.layout);

    this.drawGrid(priceScale);
    this.drawStudyPanes(panes, !this.data.indicators.some(series => series.code === "VOLUME"));
    this.drawCandles(priceScale);
    this.drawComparisonSeries();
    this.drawIndicators(priceScale, overlays);
    if (this.showMarkers) this.drawMarkers(priceScale);
    this.drawDrawings(priceScale);
    this.drawAxes(priceScale);
    this.drawLatestPrice(priceScale);
    this.drawCrosshair(priceScale);
    const markerCount = visible.reduce((count, bar) => count + this.visibleMarkerCount(bar.date), 0);
    this.lastDrawWorkUnitsValue = 16 + visible.length * (1 + active.length + this.data.comparisonSeries.length) + markerCount + this.drawingState.drawings.length;
  }

  resetView(): void {
    this.viewEnd = this.data.bars.length - 1;
    this.viewStart = Math.max(0, this.viewEnd - Math.min(110, this.data.bars.length - 1));
    this.draw();
  }

  setMarkerVisibility(visible: boolean): void {
    this.showMarkers = visible;
    this.draw();
  }

  private drawCandles(priceScale: PriceScale): void {
    if (!this.layout) return;
    for (let i = this.viewStart; i <= this.viewEnd; i++) {
      const bar = this.data.bars[i];
      const c = mapCandle(bar, i, this.viewStart, this.viewEnd, this.layout, priceScale);
      const color = c.isUp ? this.theme.up : this.theme.down;
      this.ctx.strokeStyle = color;
      this.ctx.fillStyle = color;
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(c.x, c.highY);
      this.ctx.lineTo(c.x, c.lowY);
      this.ctx.stroke();
      this.ctx.fillRect(c.x - c.candleWidth / 2, c.bodyTop, c.candleWidth, c.bodyHeight);
    }
  }

  private drawComparisonSeries(): void {
    if (!this.layout) return;
    const colors = ["#2563eb", "#7c3aed", "#0891b2"];
    this.data.comparisonSeries.forEach((series, seriesIndex) => {
      const visible: { index: number; value: number }[] = [];
      for (let index = this.viewStart; index <= this.viewEnd; index++) {
        const value = series.pointsByDate[this.data.bars[index].date];
        if (Number.isFinite(value)) visible.push({ index, value });
      }
      if (visible.length === 0) return;
      const values = visible.map(point => point.value);
      const minimum = Math.min(...values);
      const maximum = Math.max(...values);
      const range = maximum === minimum ? 1 : maximum - minimum;
      this.ctx.strokeStyle = colors[seriesIndex];
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      visible.forEach((point, pointIndex) => {
        const x = xForIndex(this.layout!, this.viewStart, this.viewEnd, point.index);
        const y = this.layout!.top + ((maximum - point.value) / range) * this.layout!.plotHeight;
        if (pointIndex === 0) this.ctx.moveTo(x, y);
        else this.ctx.lineTo(x, y);
      });
      this.ctx.stroke();
    });
  }

  private drawDrawings(priceScale: PriceScale): void {
    if (!this.layout) return;
    const left = this.layout.left;
    const right = this.layout.width - this.layout.right;
    const top = this.layout.top;
    const bottom = this.layout.top + this.layout.plotHeight;
    const transform = this.drawingTransform(priceScale);
    this.ctx.save?.(); this.ctx.beginPath(); this.ctx.rect?.(left, top, right - left, bottom - top); this.ctx.clip?.();
    this.drawingState.drawings.forEach(stored => {
      if (stored.anchors && (!transform || !transform.matches(stored.anchors))) return;
      const drawing = stored.anchors ? { ...stored, start: transform!.project(stored.anchors.start), end: transform!.project(stored.anchors.end) } : stored;
      const start = drawingCanvasPoint(drawing.start, this.layout!);
      const end = drawingCanvasPoint(drawing.end, this.layout!);
      this.ctx.strokeStyle = drawing.id === this.drawingState.selectedId ? "#2563eb" : this.theme.axis;
      this.ctx.fillStyle = "rgba(37, 99, 235, 0.08)";
      this.ctx.lineWidth = drawing.id === this.drawingState.selectedId ? 2 : 1.25;
      this.ctx.beginPath();
      switch (drawing.kind) {
        case "horizontalLine":
          this.ctx.moveTo(left, start.y);
          this.ctx.lineTo(right, start.y);
          break;
        case "verticalLine":
          this.ctx.moveTo(start.x, top);
          this.ctx.lineTo(start.x, bottom);
          break;
        case "trendLine":
          this.ctx.moveTo(start.x, start.y);
          this.ctx.lineTo(end.x, end.y);
          break;
        case "ray": {
          const terminal = rayTerminal(start, end, left, right, top, bottom);
          this.ctx.moveTo(start.x, start.y);
          this.ctx.lineTo(terminal.x, terminal.y);
          break;
        }
        case "rectangle":
          this.ctx.moveTo(start.x, start.y);
          this.ctx.lineTo(end.x, start.y);
          this.ctx.lineTo(end.x, end.y);
          this.ctx.lineTo(start.x, end.y);
          this.ctx.closePath();
          this.ctx.fill();
          break;
        case "fibonacci":
          fibonacciLevels(drawing).forEach(level => {
            const y = start.y + (end.y - start.y) * level;
            this.ctx.moveTo(Math.min(start.x, end.x), y);
            this.ctx.lineTo(Math.max(start.x, end.x), y);
          });
          break;
      }
      this.ctx.stroke();
      if (drawing.id === this.drawingState.selectedId) this.drawDrawingHandles(drawing);
    });
    this.ctx.restore?.();
  }

  private drawDrawingHandles(drawing: BridgeChartDrawing): void {
    if (!this.layout) return;
    [drawing.start, drawing.end].forEach(point => {
      const canvasPoint = drawingCanvasPoint(point, this.layout!);
      this.ctx.fillStyle = "#ffffff";
      this.ctx.strokeStyle = "#2563eb";
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      this.ctx.arc(canvasPoint.x, canvasPoint.y, 4, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.stroke();
    });
  }

  private drawIndicators(priceScale: PriceScale, overlays: NormalizedIndicatorSeries[]): void {
    if (!this.layout) return;
    overlays.forEach((series, index) => {
      const points = mapOverlayPoints(series, this.data, this.viewStart, this.viewEnd, this.layout!, priceScale);
      if (!points.length) return;
      this.ctx.strokeStyle = series.code.indexOf("EMA") === 0 ? this.theme.ema : index % 2 === 0 ? this.theme.sma : "#0e9384";
      this.ctx.lineWidth = 1.7;
      this.strokeStudySegments(points);
    });
  }

  private drawStudyPanes(panes: NormalizedIndicatorSeries[], renderPersistedVolume: boolean): void {
    if (!this.layout) return;
    const byCode = new Map(panes.map(series => [series.code, series]));
    this.layout.panes.slice(1).forEach(pane => {
      const series = byCode.get(pane.code as BridgeChartStudyCode);
      const label = series?.label ?? (pane.code === "VOLUME" && renderPersistedVolume ? "Volume" : String(pane.code));
      this.ctx.strokeStyle = this.theme.grid;
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(this.layout!.left, pane.top);
      this.ctx.lineTo(this.layout!.width - this.layout!.right, pane.top);
      this.ctx.stroke();
      this.ctx.fillStyle = this.theme.muted;
      this.ctx.font = "10px system-ui, sans-serif";
      this.ctx.textAlign = "left";
      this.ctx.textBaseline = "top";
      this.ctx.fillText(label, this.layout!.left + 3, pane.top + 2);
      if (!series && pane.code === "VOLUME" && renderPersistedVolume) {
        this.drawPersistedVolumePane(pane);
        return;
      }
      if (!series) return;
      const points = mapPanePoints(series, this.data, this.viewStart, this.viewEnd, this.layout!, pane);
      if (points.length === 0) return;
      if (series.code === "VOLUME") {
        const width = Math.max(1, this.barWidth() * 0.64);
        this.ctx.fillStyle = this.theme.volume;
        const bottom = pane.top + pane.height - 2;
        points.forEach(point => this.ctx.fillRect(point.x - width / 2, point.y, width, Math.max(1, bottom - point.y)));
        return;
      }
      this.ctx.strokeStyle = series.code === "RSI_14" ? "#8b5cf6" : series.code === "MACD_12_26_9" ? "#0e9384" : this.theme.ema;
      this.ctx.lineWidth = 1.5;
      this.strokeStudySegments(points);
    });
  }

  private strokeStudySegments(points: readonly { date: string; x: number; y: number }[]): void {
    this.ctx.beginPath();
    this.ctx.fillStyle = this.ctx.strokeStyle;
    points.forEach((point, index) => {
      const barIndex = this.data.dateIndex[point.date];
      const followsPrevious = index > 0 && this.data.dateIndex[points[index - 1].date] === barIndex - 1;
      const precedesNext = index + 1 < points.length && this.data.dateIndex[points[index + 1].date] === barIndex + 1;
      if (followsPrevious) this.ctx.lineTo(point.x, point.y);
      else this.ctx.moveTo(point.x, point.y);
      // A missing trading-session observation is a gap, not a interpolated value.
      if (!followsPrevious && !precedesNext) this.ctx.fillRect(point.x - 1.5, point.y - 1.5, 3, 3);
    });
    this.ctx.stroke();
  }

  private drawPersistedVolumePane(pane: { top: number; height: number }): void {
    if (!this.layout) return;
    const visible = this.data.bars.slice(this.viewStart, this.viewEnd + 1);
    const maximum = Math.max(1, ...visible.map(bar => bar.volume || 0));
    const width = Math.max(1, this.barWidth() * 0.64);
    const bottom = pane.top + pane.height - 2;
    this.ctx.fillStyle = this.theme.volume;
    visible.forEach((bar, offset) => {
      const x = xForIndex(this.layout!, this.viewStart, this.viewEnd, this.viewStart + offset);
      const height = (bar.volume || 0) / maximum * Math.max(1, pane.height - 8);
      this.ctx.fillRect(x - width / 2, bottom - height, width, Math.max(1, height));
    });
  }

  private drawMarkers(priceScale: PriceScale): void {
    if (!this.layout) return;
    const markerLimit = performanceBudget(this.layout.width).maxMarkersPerBar;
    for (let index = this.viewStart; index <= this.viewEnd; index++) {
      const date = this.data.bars[index]?.date;
      if (!date) continue;
      const strategyMarkers = this.strategyMarkersByDate.get(date)?.slice(0, markerLimit) ?? [];
      const tradeIdeaMarkers = this.tradeIdeaMarkersByDate.get(date)?.slice(0, Math.max(0, markerLimit - strategyMarkers.length)) ?? [];
      const intelligenceMarkers = this.intelligenceMarkersByDate.get(date)?.slice(0, Math.max(0, markerLimit - strategyMarkers.length - tradeIdeaMarkers.length)) ?? [];
      strategyMarkers.forEach(signal => {
        const point = mapMarker(signal, this.data, this.viewStart, this.viewEnd, this.layout!, priceScale, 10);
        if (point) drawTriangle(this.ctx, point.x, point.y, point.down, signal.evidenceBacked ? this.theme.signal : this.theme.axis);
      });
      tradeIdeaMarkers.forEach(idea => {
        const point = mapMarker(idea, this.data, this.viewStart, this.viewEnd, this.layout!, priceScale, 21);
        if (!point) return;
        this.ctx.fillStyle = this.theme.idea;
        this.ctx.strokeStyle = "#fff";
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.arc(point.x, point.y, 5, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();
      });
      resolveMarkerCollisions(intelligenceMarkers).forEach(item => {
        const point = mapMarker({
          date: item.marker.date,
          strategy: item.marker.kind,
          direction: item.marker.direction,
          evidenceBacked: true
        }, this.data, this.viewStart, this.viewEnd, this.layout!, priceScale, 32 + item.collisionIndex * 9);
        if (!point) return;
        this.ctx.fillStyle = markerColor(item.marker.kind);
        this.ctx.beginPath();
        this.ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
        this.ctx.fill();
      });
    }
  }

  private drawAxes(priceScale: PriceScale): void {
    if (!this.layout) return;
    this.ctx.fillStyle = this.theme.muted;
    this.ctx.font = "11px system-ui, sans-serif";
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "middle";
    priceScale.ticks.forEach(tick => {
      this.ctx.fillText(formatNumber(tick), this.layout!.width - this.layout!.right + 8, priceScale.y(tick));
    });
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "top";
    const span = this.viewEnd - this.viewStart + 1;
    const step = Math.max(1, Math.floor(span / 5));
    for (let i = this.viewStart; i <= this.viewEnd; i += step) {
      this.ctx.fillText(shortDate(this.data.bars[i].date), xForIndex(this.layout, this.viewStart, this.viewEnd, i), this.layout.height - this.layout.bottom + 8);
    }
  }

  private drawGrid(priceScale: PriceScale): void {
    if (!this.layout) return;
    this.ctx.strokeStyle = this.theme.grid;
    this.ctx.lineWidth = 1;
    priceScale.ticks.forEach(tick => {
      const y = priceScale.y(tick);
      this.ctx.beginPath();
      this.ctx.moveTo(this.layout!.left, y);
      this.ctx.lineTo(this.layout!.width - this.layout!.right, y);
      this.ctx.stroke();
    });
    const step = Math.max(1, Math.floor((this.viewEnd - this.viewStart + 1) / 5));
    for (let index = this.viewStart; index <= this.viewEnd; index += step) {
      const x = xForIndex(this.layout, this.viewStart, this.viewEnd, index);
      this.ctx.beginPath();
      this.ctx.moveTo(x, this.layout.top);
      this.ctx.lineTo(x, this.layout.contentBottom);
      this.ctx.stroke();
    }
    this.ctx.strokeStyle = this.theme.axis;
    this.ctx.beginPath();
    this.ctx.moveTo(this.layout.width - this.layout.right, this.layout.top);
    this.ctx.lineTo(this.layout.width - this.layout.right, this.layout.contentBottom);
    this.ctx.moveTo(this.layout.left, this.layout.contentBottom);
    this.ctx.lineTo(this.layout.width - this.layout.right, this.layout.contentBottom);
    this.ctx.stroke();
  }

  private drawLatestPrice(priceScale: PriceScale): void {
    if (!this.layout || this.canvas.dataset?.bridgeChartFill !== "true" || this.viewEnd !== this.data.bars.length - 1) return;
    const bar = this.data.bars[this.viewEnd];
    const y = priceScale.y(bar.close);
    const right = this.layout.width - this.layout.right;
    const color = bar.close >= bar.open ? this.theme.up : this.theme.down;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([4, 4]);
    this.ctx.beginPath();
    this.ctx.moveTo(this.layout.left, y);
    this.ctx.lineTo(right, y);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
    this.ctx.fillStyle = color;
    this.ctx.fillRect(right, y - 10, this.layout.right, 20);
    this.ctx.fillStyle = "#10202a";
    this.ctx.font = "11px system-ui, sans-serif";
    this.ctx.textBaseline = "middle";
    this.ctx.textAlign = "left";
    this.ctx.fillText(formatNumber(bar.close), right + 6, y);
  }

  private drawCrosshair(priceScale: PriceScale): void {
    if (!this.layout || !this.crosshair) {
      if (this.tooltip) this.tooltip.hidden = true;
      return;
    }
    const x = clamp(this.crosshair.x, this.layout.left, this.layout.width - this.layout.right);
    const y = clamp(this.crosshair.y, this.layout.top, this.layout.contentBottom);
    const index = this.indexFromX(x);
    const bar = this.data.bars[index];
    if (!bar) return;

    const cx = xForIndex(this.layout, this.viewStart, this.viewEnd, index);
    this.ctx.strokeStyle = this.theme.crosshair;
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([4, 4]);
    this.ctx.beginPath();
    this.ctx.moveTo(cx, this.layout.top);
    this.ctx.lineTo(cx, this.layout.contentBottom);
    this.ctx.moveTo(this.layout.left, y);
    this.ctx.lineTo(this.layout.width - this.layout.right, y);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
    this.showTooltip(index, bar.date);
  }

  private showTooltip(index: number, date: string): void {
    if (!this.tooltip || !this.layout) return;
    const bar = this.data.bars[index];
    const indicators = this.activeStudies().map(series => {
      const point = series.pointsByDate[bar.date];
      return point === undefined ? null : `<div>${escapeHtml(series.label)}: ${formatNumber(point)}</div>`;
    }).filter(Boolean).join("");
    const markerLimit = performanceBudget(this.layout.width).maxMarkersPerBar;
    const signals = this.strategyMarkersByDate.get(bar.date)?.slice(0, markerLimit) ?? [];
    const signalHtml = signals.map(signal =>
      `<div>${escapeHtml(signal.strategy)} ${escapeHtml(signal.direction)} · ${Math.round(Number(signal.confidence || 0) * 100)}%</div>`
    ).join("");
    const evidenceHtml = signals.map(signal => {
      const evidence = this.data.evidenceByKey[evidenceKey(signal.strategy, signal.direction)];
      if (!evidence) return "";
      return `<div>Evidence: n=${evidence.sampleSize} · win ${formatNumber(evidence.winRate)}% · avg ${formatNumber(evidence.averageReturn)}%</div>`;
    }).join("");
    const idea = this.tradeIdeaMarkersByDate.get(bar.date)?.[0];
    const ideaHtml = idea ? "<div>Trade idea marker · evidence-backed</div>" : "";
    const intelligenceHtml = (this.intelligenceMarkersByDate.get(bar.date) ?? [])
      .slice(0, markerLimit)
      .map(marker => `<div>${escapeHtml(marker.labelEn)} / ${escapeHtml(marker.labelFa)} · ${escapeHtml(marker.evidence.summary)}</div>`)
      .join("");
    this.tooltip.innerHTML =
      `<strong>${escapeHtml(bar.date)}</strong>` +
      `<div>O ${formatNumber(bar.open)} · H ${formatNumber(bar.high)} · L ${formatNumber(bar.low)} · C ${formatNumber(bar.close)}</div>` +
      `<div>V ${formatNumber(bar.volume)}</div>${indicators}${signalHtml}${evidenceHtml}${ideaHtml}${intelligenceHtml}`;
    this.tooltip.hidden = false;
    this.tooltip.style.left = `${clamp(xForIndex(this.layout, this.viewStart, this.viewEnd, index), 0, this.layout.width - 280)}px`;
    this.tooltip.style.top = `${clamp(this.crosshair ? this.crosshair.y : 0, 0, this.layout.height - 150)}px`;
  }

  private activeStudies(): NormalizedIndicatorSeries[] {
    return this.data.indicators.filter(series => series.available && this.enabledStudyCodes.has(series.code));
  }

  private visibleActiveStudies(visibleBars: readonly { date: string }[]): NormalizedIndicatorSeries[] {
    return this.activeStudies().map(series => ({
      ...series,
      points: visibleBars.flatMap(bar => {
        const value = series.pointsByDate[bar.date];
        return value === undefined ? [] : [{ date: bar.date, value }];
      })
    }));
  }

  private rebuildMarkerIndexes(): void {
    this.strategyMarkersByDate = indexByDate(this.data.strategyMarkers);
    this.tradeIdeaMarkersByDate = indexByDate(this.data.tradeIdeaMarkers);
    this.intelligenceMarkersByDate = indexByDate(this.intelligenceMarkers);
  }

  private visibleMarkerCount(date: string): number {
    if (!this.showMarkers) return 0;
    const limit = performanceBudget(this.layout?.width ?? 720).maxMarkersPerBar;
    return Math.min(limit,
      (this.strategyMarkersByDate.get(date)?.length ?? 0) +
      (this.tradeIdeaMarkersByDate.get(date)?.length ?? 0) +
      (this.intelligenceMarkersByDate.get(date)?.length ?? 0));
  }

  private constrainViewToBudget(): void {
    if (this.data.bars.length === 0) return;
    const maximum = performanceBudget(this.layout?.width ?? 720).maxVisibleBars;
    const span = this.viewEnd - this.viewStart + 1;
    if (span <= maximum) return;
    this.viewStart = Math.max(0, this.viewEnd - maximum + 1);
  }

  private renderedPaneCodes(): BridgeChartStudyCode[] {
    const codes = this.activeStudies().filter(series => series.kind === "Pane").map(series => series.code);
    const hasVolumeContract = this.data.indicators.some(series => series.code === "VOLUME");
    if (!hasVolumeContract && this.studyDefaults === undefined && !codes.includes("VOLUME")) codes.unshift("VOLUME");
    return codes;
  }

  private ensurePaneLayout(): void {
    if (!this.layout) return;
    const expected = this.renderedPaneCodes();
    const current = this.layout.panes.slice(1).map(pane => pane.code);
    if (expected.length !== current.length || expected.some((code, index) => code !== current[index])) this.rebuildLayout();
  }

  private rebuildLayout(): void {
    if (!this.layout) return;
    this.layout = buildLayout(this.layout.width, this.layout.height, this.renderedPaneCodes(), this.paneOptions);
  }

  private resetStudies(): void {
    this.enabledStudyCodes.clear();
    this.data.indicators.filter(series => series.available && (this.studyDefaults === undefined || this.studyDefaults.includes(series.code)))
      .forEach(series => this.enabledStudyCodes.add(series.code));
  }
}

function performanceBudget(width: number): BridgeChartPerformanceBudget {
  if (width <= 480) return { profile: "phone", maxVisibleBars: 120, maxMarkersPerBar: 6, maxDrawWorkUnits: 2_400, maxRetainedBars: 10_000, maxIncrementalBars: 512 };
  if (width <= 1_024) return { profile: "tablet", maxVisibleBars: 240, maxMarkersPerBar: 8, maxDrawWorkUnits: 4_800, maxRetainedBars: 10_000, maxIncrementalBars: 512 };
  return { profile: "desktop", maxVisibleBars: 360, maxMarkersPerBar: 10, maxDrawWorkUnits: 7_200, maxRetainedBars: 10_000, maxIncrementalBars: 512 };
}

function indexByDate<T extends { date: string }>(items: readonly T[]): Map<string, T[]> {
  const indexed = new Map<string, T[]>();
  items.forEach(item => indexed.set(item.date, [...(indexed.get(item.date) ?? []), item]));
  return indexed;
}

function markerColor(kind: string): string {
  switch (kind) {
    case "Strategy": return "#2563eb";
    case "Backtest": return "#7c3aed";
    case "WalkForward": return "#0891b2";
    case "CompositeAi": return "#0e9384";
    case "Codal": return "#d97706";
    case "MaterialRisk": return "#dc2626";
    default: return "#64748b";
  }
}

function clonePaneOptions(value: BridgeChartPaneOptions): BridgeChartPaneOptions {
  return { ...value, heights: { ...(value.heights ?? {}) } };
}

function drawingCanvasPoint(point: BridgeChartDrawingPoint, layout: ChartLayout): PointerPoint {
  return {
    x: layout.left + point.x * layout.plotWidth,
    y: layout.top + point.y * layout.plotHeight
  };
}

function rayTerminal(
  start: PointerPoint,
  end: PointerPoint,
  left: number,
  right: number,
  top: number,
  bottom: number
): PointerPoint {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return end;
  const candidates: number[] = [];
  if (dx > 0) candidates.push((right - start.x) / dx);
  if (dx < 0) candidates.push((left - start.x) / dx);
  if (dy > 0) candidates.push((bottom - start.y) / dy);
  if (dy < 0) candidates.push((top - start.y) / dy);
  const boundaries = candidates.filter(value => Number.isFinite(value) && value >= 1);
  const scale = boundaries.length ? Math.min(...boundaries) : 1;
  return { x: start.x + dx * scale, y: start.y + dy * scale };
}

function drawTriangle(ctx: CanvasRenderingContext2D, x: number, y: number, down: boolean, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  if (down) {
    ctx.moveTo(x, y + 6);
    ctx.lineTo(x - 6, y - 5);
    ctx.lineTo(x + 6, y - 5);
  } else {
    ctx.moveTo(x, y - 6);
    ctx.lineTo(x - 6, y + 5);
    ctx.lineTo(x + 6, y + 5);
  }
  ctx.closePath();
  ctx.fill();
}
